import { useQuery } from "@tanstack/react-query";
import { supabaseV2 } from "./supabase";
import { useSeasonScope } from "./seasons";
import type { Session } from "../types";

export function useTeamOpponents(teamId: string) {
  return useQuery({
    queryKey: ["opponents", teamId, "names"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("opponents")
        .select("id, name")
        .eq("team_id", teamId)
        .order("name");
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string }>;
    },
    staleTime: 60_000,
  });
}

export function useTeamCompetitions(teamId: string) {
  return useQuery({
    queryKey: ["competitions", teamId, "names"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("competitions")
        .select("id, name, type, is_active")
        .eq("team_id", teamId)
        .order("name");
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string; type: string | null; is_active: boolean }>;
    },
    staleTime: 60_000,
  });
}

export function useEvents(teamId: string, kind: "training" | "match_day") {
  const wantsMatches = kind === "match_day";
  const scope = useSeasonScope();
  return useQuery({
    queryKey: ["events", teamId, kind, scope.key],
    enabled: scope.ready,
    queryFn: async () => {
      let query = supabaseV2
        .from("sessions")
        .select("*")
        .eq("team_id", teamId)
        .eq("has_matches", wantsMatches);
      if (scope.seasonId) query = query.eq("season_id", scope.seasonId);
      const { data, error } = await query.order("starts_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Session[];
    },
  });
}
